import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/lib/apiClient";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { reportsExtendedApi } from "../../services/reports-extended.api";
import { useReportData } from "../../hooks/useReportData";
import { Document, Book, Lock, Recent } from "@/components/icons";
import styles from "./LibraryConsumptionReportPage.module.scss";

// ────────────────────────────────────────────────────────────────────────────
// Types (match backend)
// ────────────────────────────────────────────────────────────────────────────

export type LibraryShareType = "EVERYONE" | "ADMINS" | "PRIVATE";

export interface LibraryConsumptionItem {
  itemID: string;
  title: string;
  type: "FOLDER" | "FILE";
  shareType: LibraryShareType;
  mimeType: string | null;
  sizeBytes: number | null;
  createdByName: string | null;
  sharedDepartments: number;
  sharedUsers: number;
  createdAt: string;
}

export interface LibraryConsumptionBucket {
  label: string;
  count: number;
}

export interface LibraryConsumptionMonthlyPoint {
  month: string;
  files: number;
  folders: number;
}

export interface LibraryConsumptionResponse {
  summary: {
    totalItems: number;
    totalFiles: number;
    totalFolders: number;
    totalShares: number;
    avgSharesPerItem: number;
    privateRatePercent: number;
  };
  byShareType: LibraryConsumptionBucket[];
  byFileType: LibraryConsumptionBucket[];
  byDepartment: LibraryConsumptionBucket[];
  monthlyTrend: LibraryConsumptionMonthlyPoint[];
  items: LibraryConsumptionItem[];
  count: number;
}

export interface LibraryConsumptionParams {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  departmentID?: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Constants & helpers
// ────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;
const ENDPOINT = "/api/admin/reports/library-consumption";

const SHARE_COLORS: Record<LibraryShareType, string> = {
  EVERYONE: "#10b981",
  ADMINS: "#0ea5e9",
  PRIVATE: "#ef4444",
};

const formatBytes = (bytes: number | null): string => {
  if (bytes == null || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(size < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const fetchLibraryConsumption = async (
  params: LibraryConsumptionParams,
  signal: AbortSignal,
): Promise<LibraryConsumptionResponse> => {
  const { data } = await apiClient.get(ENDPOINT, { params, signal });
  if (data && typeof data === "object" && "data" in data) {
    return (data as { data: LibraryConsumptionResponse }).data;
  }
  return data as LibraryConsumptionResponse;
};

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

const LibraryConsumptionReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: LibraryConsumptionParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      departmentID: filter.departmentID,
      search: filter.search?.trim() || undefined,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    LibraryConsumptionResponse,
    LibraryConsumptionParams
  >({
    fetcher: fetchLibraryConsumption,
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;
  const monthlyTrend = data?.monthlyTrend ?? [];
  const byShareType = data?.byShareType ?? [];
  const byFileType = data?.byFileType ?? [];

  // Donut: ensure deterministic order for color mapping
  const shareDonutLabels = byShareType.map((b) => b.label);
  const shareDonutSeries = byShareType.map((b) => b.count);
  const shareDonutColors = shareDonutLabels.map(
    (l) => SHARE_COLORS[l as LibraryShareType] ?? "#94a3b8",
  );

  const columns: TableColumn<LibraryConsumptionItem>[] = [
    {
      header: t("reports.libraryConsumption.headerTitle"),
      accessor: (row) => <span className={styles.titleCell}>{row.title}</span>,
    },
    {
      header: t("reports.libraryConsumption.headerType"),
      accessor: (row) => (
        <span
          className={
            row.type === "FOLDER" ? styles.badgeFolder : styles.badgeFile
          }
        >
          {row.type === "FOLDER"
            ? t("reports.libraryConsumption.typeFolder")
            : t("reports.libraryConsumption.typeFile")}
        </span>
      ),
    },
    {
      header: t("reports.libraryConsumption.headerShare"),
      accessor: (row) => (
        <span
          className={styles.shareBadge}
          style={{ background: `${SHARE_COLORS[row.shareType]}1a`, color: SHARE_COLORS[row.shareType] }}
        >
          {t(`reports.libraryConsumption.share.${row.shareType.toLowerCase()}`)}
        </span>
      ),
    },
    {
      header: t("reports.libraryConsumption.headerMime"),
      accessor: (row) => (
        <span className={styles.muted}>{row.mimeType ?? "—"}</span>
      ),
    },
    {
      header: t("reports.libraryConsumption.headerSize"),
      accessor: (row) => formatBytes(row.sizeBytes),
    },
    {
      header: t("reports.libraryConsumption.headerCreatedBy"),
      accessor: (row) => row.createdByName ?? "—",
    },
    {
      header: t("reports.libraryConsumption.headerSharedDepartments"),
      accessor: (row) => row.sharedDepartments,
    },
    {
      header: t("reports.libraryConsumption.headerSharedUsers"),
      accessor: (row) => row.sharedUsers,
    },
    {
      header: t("reports.libraryConsumption.headerCreatedAt"),
      accessor: (row) => formatDate(row.createdAt),
    },
  ];

  return (
    <ReportDetailLayout
      title={t("reports.libraryConsumption.title")}
      description={t("reports.libraryConsumption.description")}
      categoryLabel={t("reportCatalog.categories.engagement.title")}
      exportSlot={
        <ReportExportButtons
          reportType="LIBRARY_CONSUMPTION"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="library-consumption"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
        />
      }
    >
      {/* KPI Grid */}
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t("reports.libraryConsumption.kpiTotalFiles")}
          value={summary?.totalFiles ?? 0}
          icon={<Document width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.libraryConsumption.kpiTotalFolders")}
          value={summary?.totalFolders ?? 0}
          icon={<Book width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.libraryConsumption.kpiTotalShares")}
          value={summary?.totalShares ?? 0}
          icon={<Recent width={18} height={18} />}
          accent="success"
        />
        <ReportKPICard
          title={t("reports.libraryConsumption.kpiPrivateRate")}
          value={summary ? `${summary.privateRatePercent.toFixed(1)}%` : "—"}
          icon={<Lock width={18} height={18} />}
          accent={
            summary && summary.privateRatePercent > 60
              ? "danger"
              : summary && summary.privateRatePercent > 30
                ? "warning"
                : "success"
          }
        />
      </div>

      {/* Charts row: trend (full width), share + filetype (split) */}
      <ReportChart
        title={t("reports.libraryConsumption.chartTrendTitle")}
        subtitle={t("reports.libraryConsumption.chartTrendSubtitle")}
        type="line"
        height={300}
        isLoading={isLoading}
        isEmpty={monthlyTrend.length === 0}
        series={[
          {
            name: t("reports.libraryConsumption.seriesFiles"),
            data: monthlyTrend.map((m) => m.files),
          },
          {
            name: t("reports.libraryConsumption.seriesFolders"),
            data: monthlyTrend.map((m) => m.folders),
          },
        ]}
        options={{
          xaxis: { categories: monthlyTrend.map((m) => m.month) },
          stroke: { curve: "smooth", width: 3 },
          markers: { size: 4 },
          colors: ["#4f46e5", "#f59e0b"],
        }}
      />

      <div className={styles.chartsRow}>
        <ReportChart
          title={t("reports.libraryConsumption.chartShareTypeTitle")}
          type="donut"
          height={320}
          isLoading={isLoading}
          isEmpty={byShareType.length === 0}
          series={shareDonutSeries}
          options={{
            labels: shareDonutLabels.map((l) =>
              t(`reports.libraryConsumption.share.${l.toLowerCase()}`),
            ),
            colors: shareDonutColors,
            legend: { position: "bottom" },
            plotOptions: {
              pie: {
                donut: {
                  size: "65%",
                  labels: {
                    show: true,
                    total: {
                      show: true,
                      label: t("reports.libraryConsumption.donutTotal"),
                    },
                  },
                },
              },
            },
          }}
        />

        <ReportChart
          title={t("reports.libraryConsumption.chartFileTypeTitle")}
          subtitle={t("reports.libraryConsumption.chartFileTypeSubtitle")}
          type="bar"
          height={320}
          isLoading={isLoading}
          isEmpty={byFileType.length === 0}
          series={[
            {
              name: t("reports.libraryConsumption.seriesCount"),
              data: byFileType.map((b) => b.count),
            },
          ]}
          options={{
            xaxis: { categories: byFileType.map((b) => b.label) },
            plotOptions: {
              bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
            },
            colors: ["#0ea5e9"],
          }}
        />
      </div>

      <Table
        columns={columns}
        data={items}
        isLoading={isLoading}
        contentBasedHeight
        getRowId={(row) => row.itemID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t("reports.libraryConsumption.noData")
            : t("reports.libraryConsumption.noItems")
        }
      />
    </ReportDetailLayout>
  );
};

export default LibraryConsumptionReportPage;
